import { useEffect, useRef, useState } from 'react';
import { channelName, type SegmentRule, type Tag, type TenantChannel } from '@wa/shared';
import { blankRule, fieldOf, groupsOf, operatorsFor, type FilterField } from '@/customers/filters';

/**
 * The reference's filter builder: groups of conditions, ANDed inside a group and
 * ORed between groups, each condition a field, an operator and a value drawn as
 * chips on a "where …" line.
 *
 * It edits a flat rule list with a group number on each rule — the shape the
 * server stores for a segment — so "Apply" for an ad-hoc filter and "Save" for a
 * segment send the same thing. Nothing here knows which of the two it is.
 */
export function FilterBuilder({ rules, onChange, fields, tags, channels, onApply, canApply, applyLabel = 'Apply filters', onClear }: {
  rules: SegmentRule[];
  onChange: (rules: SegmentRule[]) => void;
  fields: FilterField[];
  tags: Tag[];
  channels: TenantChannel[];
  onApply: () => void;
  canApply: boolean;
  applyLabel?: string;
  onClear?: () => void;
}) {
  const groups = groupsOf(rules);
  const shownGroups = groups.length ? groups : [0];

  const setRule = (index: number, next: SegmentRule) => onChange(rules.map((r, i) => (i === index ? next : r)));
  const removeRule = (index: number) => onChange(rules.filter((_, i) => i !== index));
  const removeGroup = (g: number) => onChange(rules.filter((r) => (r.group ?? 0) !== g));
  const addRule = (g: number) => onChange([...rules, blankRule(fields[0]!, g)]);
  const addGroup = () => addRule((groups[groups.length - 1] ?? -1) + 1);

  return (
    <div className="flex flex-col gap-3">
      {shownGroups.map((g, gi) => {
        const members = rules.map((r, i) => ({ r, i })).filter(({ r }) => (r.group ?? 0) === g);
        return (
          <div key={g} className="rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 pt-2 pb-3">
            <div className="flex items-center justify-between">
              <span className="rounded-(--radius-chip) bg-(--color-sunken) px-2 py-0.5 text-micro font-medium text-(--color-ink-soft)">Filter group {gi + 1}</span>
              <button type="button" onClick={() => removeGroup(g)} aria-label={`Remove filter group ${gi + 1}`} className="tap grid size-8 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-surface) hover:text-(--color-bad)">
                <IconTrash />
              </button>
            </div>
            {members.map(({ r, i }, n) => (
              <Condition
                key={i} rule={r} first={n === 0} fields={fields} tags={tags} channels={channels}
                onChange={(next) => setRule(i, next)} onRemove={() => removeRule(i)}
              />
            ))}
            <button type="button" onClick={() => addRule(g)} className="tap mt-2 inline-flex items-center gap-1.5 rounded-(--radius-field) px-2 py-1 text-body font-medium text-(--color-brand) hover:bg-(--color-surface)">
              <IconPlus /> Add condition
            </button>
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-3">
        {rules.length > 0 && (
          <button type="button" onClick={onApply} disabled={!canApply} className="tap h-10 rounded-(--radius-field) bg-(--color-brand) px-4 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-50">
            {applyLabel}
          </button>
        )}
        <button type="button" onClick={addGroup} className="tap inline-flex items-center gap-1.5 rounded-(--radius-field) px-2 py-1 text-body font-medium text-(--color-brand) hover:bg-(--color-sunken)">
          <IconPlus /> Add filter group
        </button>
        {onClear && rules.length > 0 && (
          <button type="button" onClick={onClear} className="tap rounded-(--radius-field) px-2 py-1 text-body font-medium text-(--color-ink-soft) hover:bg-(--color-sunken)">Clear all</button>
        )}
      </div>
    </div>
  );
}

function Condition({ rule, first, fields, tags, channels, onChange, onRemove }: {
  rule: SegmentRule; first: boolean; fields: FilterField[]; tags: Tag[]; channels: TenantChannel[];
  onChange: (rule: SegmentRule) => void; onRemove: () => void;
}) {
  const field = fieldOf(rule, fields) ?? fields[0]!;
  const ops = operatorsFor(field);
  const op = ops.find((o) => o.op === rule.op) ?? ops[0]!;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5">
      <span className="w-12 text-body text-(--color-ink-soft)">{first ? 'where' : 'and'}</span>
      <FieldPicker fields={fields} value={field} onChange={(f) => onChange(blankRule(f, rule.group ?? 0))} />
      <Chip>
        <select value={op.op} onChange={(e) => onChange({ ...rule, op: e.target.value, value: opValueReset(rule, field, e.target.value) })} aria-label="Operator" className="absolute inset-0 cursor-pointer opacity-0">
          {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
        </select>
        {op.label}
      </Chip>
      {op.needsValue && <Value rule={rule} field={field} tags={tags} channels={channels} onChange={(value) => onChange({ ...rule, value })} />}
      <button type="button" onClick={onRemove} aria-label="Remove condition" className="tap ml-auto grid size-8 place-items-center rounded-full text-(--color-ink-soft) hover:bg-(--color-sunken) hover:text-(--color-bad)">
        <IconTrash />
      </button>
    </div>
  );
}

/** Switching to/from "in the last" changes what the value means, so it resets. */
function opValueReset(rule: SegmentRule, field: FilterField, op: string): string {
  if (field.value !== 'date') return rule.value ?? '';
  const wasRelative = rule.op === 'in_last' || rule.op === 'not_in_last';
  const isRelative = op === 'in_last' || op === 'not_in_last';
  return wasRelative === isRelative ? (rule.value ?? '') : isRelative ? '72' : '';
}

function Value({ rule, field, tags, channels, onChange }: {
  rule: SegmentRule; field: FilterField; tags: Tag[]; channels: TenantChannel[]; onChange: (value: string) => void;
}) {
  const value = rule.value ?? '';
  const input = 'tap h-8 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 text-body outline-none focus:border-(--color-brand)';

  if (field.value === 'tags') return <TagPicker tags={tags} value={value} onChange={onChange} />;

  if (field.value === 'channel') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Channel" className={input}>
        <option value="">Choose a number</option>
        {channels.map((c) => <option key={c.id} value={c.id}>{channelName(c)}</option>)}
      </select>
    );
  }
  if (field.kind === 'opted_out') {
    return (
      <select value={value || 'false'} onChange={(e) => onChange(e.target.value)} aria-label="Value" className={input}>
        <option value="true">Yes</option><option value="false">No</option>
      </select>
    );
  }
  if (field.value === 'bool') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Value" className={input}>
        <option value="">Choose</option><option value="true">Yes</option><option value="false">No</option>
      </select>
    );
  }
  if (field.value === 'list' && field.options) {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Value" className={input}>
        <option value="">Choose</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.value === 'date') {
    if (rule.op === 'in_last' || rule.op === 'not_in_last') {
      return <RelativeValue hours={Number(value) || 0} onChange={(h) => onChange(String(h))} className={input} />;
    }
    return <input type="date" value={value.slice(0, 10)} onChange={(e) => onChange(e.target.value)} aria-label="Date" className={input} />;
  }
  if (field.value === 'number') {
    return <input type="number" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Value" className={`${input} w-32`} />;
  }
  return <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Type a value" aria-label="Value" className={`${input} min-w-40 flex-1`} />;
}

/** "In the last N hours/days" — stored as hours, shown in whichever unit the person picked. */
function RelativeValue({ hours, onChange, className }: { hours: number; onChange: (hours: number) => void; className: string }) {
  const [unit, setUnit] = useState<'hours' | 'days'>(hours >= 24 && hours % 24 === 0 ? 'days' : 'hours');
  const shown = unit === 'days' ? Math.max(1, Math.round(hours / 24)) : hours;
  return (
    <>
      <input type="number" min={1} value={shown || ''} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0) * (unit === 'days' ? 24 : 1))} aria-label="Amount" className={`${className} w-20`} />
      <select value={unit} onChange={(e) => { const u = e.target.value as 'hours' | 'days'; setUnit(u); onChange(u === 'days' ? Math.max(1, Math.round(hours / 24)) * 24 : hours); }} aria-label="Unit" className={className}>
        <option value="hours">hours</option><option value="days">days</option>
      </select>
    </>
  );
}

function TagPicker({ tags, value, onChange }: { tags: Tag[]; value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useAway(box, open, () => setOpen(false));
  const chosen = new Set(value.split(',').filter(Boolean).map(Number));
  const toggle = (id: number) => { const next = new Set(chosen); if (next.has(id)) next.delete(id); else next.add(id); onChange([...next].join(',')); };
  const label = chosen.size === 0 ? 'Choose tags' : tags.filter((t) => chosen.has(t.id)).map((t) => t.name).join(', ');
  return (
    <div ref={box} className="relative">
      <Chip onClick={() => setOpen((o) => !o)}>{label}</Chip>
      {open && (
        <ul className="absolute top-full left-0 z-30 mt-1 max-h-64 w-56 overflow-y-auto rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)">
          {tags.length === 0 && <li className="px-3 py-2 text-small text-(--color-muted)">No tags yet.</li>}
          {tags.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => toggle(t.id)} className="tap flex w-full items-center gap-2 px-3 py-1.5 text-left text-body hover:bg-(--color-sunken)">
                <span className={`grid size-4 place-items-center rounded border ${chosen.has(t.id) ? 'border-(--color-brand) bg-(--color-brand) text-(--color-brand-ink)' : 'border-(--color-line)'}`}>{chosen.has(t.id) && <IconTick />}</span>
                {t.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The reference's field menu: a search box over every filterable field. */
export function FieldPicker({ fields, value, onChange, label }: { fields: FilterField[]; value?: FilterField; onChange: (f: FilterField) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useAway(box, open, () => setOpen(false));
  const shown = fields.filter((f) => f.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div ref={box} className="relative">
      <Chip onClick={() => { setOpen((o) => !o); setQ(''); }}>{label ?? value?.label ?? 'Choose'}</Chip>
      {open && (
        <div className="absolute top-full left-0 z-30 mt-1 w-56 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-pop)">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filter" aria-label="Search fields" className="mx-2 mb-1 w-[calc(100%-1rem)] border-b border-(--color-line) bg-transparent px-1 py-1.5 text-body outline-none placeholder:text-(--color-faint)" />
          <ul className="max-h-64 overflow-y-auto">
            {shown.map((f) => (
              <li key={f.key}>
                <button type="button" onClick={() => { onChange(f); setOpen(false); }} className="tap block w-full px-3 py-1.5 text-left text-body hover:bg-(--color-sunken)">{f.label}</button>
              </li>
            ))}
            {shown.length === 0 && <li className="px-3 py-2 text-small text-(--color-muted)">Nothing matches.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function Chip({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  const cls = 'tap relative inline-flex h-8 max-w-56 items-center truncate rounded-(--radius-field) bg-(--color-sunken) px-2.5 text-body font-medium text-(--color-ink) hover:bg-(--color-line-dim)';
  return onClick ? <button type="button" onClick={onClick} className={cls}>{children}</button> : <span className={cls}>{children}</span>;
}

export function useAway(box: React.RefObject<HTMLElement | null>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) close(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', away); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [box, open, close]);
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconTrash = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M4 6h12M8 6V4h4v2M6 6l.8 10h6.4L14 6" {...s} /></svg>;
const IconPlus = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="M10 4v12M4 10h12" {...s} /></svg>;
const IconTick = () => <svg viewBox="0 0 20 20" className="size-3" aria-hidden="true"><path d="m5 10.5 3.2 3L15 6.5" {...s} strokeWidth={2.2} /></svg>;
